/**
 * A startup's documents (F5b).
 *
 * The container half of the split plan §3.1 requires: `AttachmentsPanel` is
 * presentational and shared, and this supplies its data. A panel that fetched
 * for itself could not be shared — a feature must not import another feature,
 * and `components/domain` must not import a feature's api/ layer — so the
 * query lives on this side of the line and the rendering on the other.
 *
 * The pilot page gets its own container of the same shape. That is one small
 * file per feature, which is the price of the panel being reusable at all.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AttachmentSummary } from '@nexus/contracts';

import { AttachmentsPanel } from '../../../components/domain/AttachmentsPanel';
import { UploadDialog } from '../../../components/domain/UploadDialog';
import {
  useAttachments,
  useDeleteAttachment,
  useDownloadLink,
} from '../../../lib/attachments.queries';
import { useAuth } from '../../../lib/auth-context';
import { followDownloadLink } from '../../../lib/download';
import { useTableState } from '../../../lib/use-table-state';

/** Sort fields the API allow-lists (see `ATTACHMENT_SORT_FIELDS`). */
const SORT_FIELDS = ['fileName', 'type', 'sizeBytes', 'createdAt', 'updatedAt'];

export interface StartupDocumentsProps {
  startupId: string;
}

export function StartupDocuments({
  startupId,
}: StartupDocumentsProps): React.JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();

  const { state, toggleSort } = useTableState(SORT_FIELDS);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { data, isPending, isError } = useAttachments({
    page: 1,
    /* One page. A record's document list is a handful of files, and paging
       through a tab's contents would hide the one someone came for. */
    pageSize: 100,
    startupId,
    ...(state.sort === null
      ? {}
      : { sort: `${state.sort.field}:${state.sort.direction}` }),
  });

  const downloadLink = useDownloadLink();
  const remove = useDeleteAttachment();

  /** Asks for a signed link, then follows it — see `lib/download`. */
  const onDownload = (attachment: AttachmentSummary): void => {
    setError(undefined);
    downloadLink.mutate(attachment.id, {
      onSuccess: (link) => {
        followDownloadLink(link.url);
      },
      onError: () => {
        setError(t('attachment.download.failed'));
      },
    });
  };

  const onDelete = (attachment: AttachmentSummary): void => {
    setError(undefined);
    remove.mutate(
      { id: attachment.id, version: attachment.version },
      {
        onError: () => {
          setError(t('errors.generic'));
        },
      },
    );
  };

  return (
    <>
      <AttachmentsPanel
        attachments={data?.data ?? []}
        total={data?.meta.total ?? 0}
        isPending={isPending}
        isError={isError}
        canUpload={can('EDITOR')}
        canDelete={can('ADMIN')}
        /* Spread rather than passed as possibly-undefined:
           `exactOptionalPropertyTypes` rejects an explicit undefined. */
        {...(downloadLink.isPending ? { downloadingId: downloadLink.variables } : {})}
        isDeleting={remove.isPending}
        onUpload={() => {
          setUploadOpen(true);
        }}
        onDownload={onDownload}
        onDelete={onDelete}
        {...(error === undefined ? {} : { error })}
        sort={state.sort}
        onSortChange={toggleSort}
      />

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        startupId={startupId}
      />
    </>
  );
}
